import { Favs } from "../../Favorites/Favs";
import {
  TacticApiClient,
  AuthenticatedUser,
  IUserFull,
  TableOverviewUser
} from "@gettactic/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { classNames } from "@gettactic/helpers";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/solid";
import { FavoriteIcon } from "@cmp-src/icons/icons";
import { Star } from "lucide-react";

interface UserCellProps {
  row: {
    original: {
      user: AuthenticatedUser["user"] | TableOverviewUser["user"];
    };
  };
  favsApi?: TacticApiClient;
  isMe: boolean;
  hasAnyFav: boolean;
}

/**
 * The name cell of a team week row. With `favsApi`, hovering it turns the star into the
 * favorites groups picker (not on your own row, `isMe`).
 */
export const UserCell: React.FC<UserCellProps> = ({
  row,
  favsApi,
  isMe,
  hasAnyFav
}) => {
  return (
    <UserComponent
      user={row.original.user}
      hasAnyFav={hasAnyFav}
      favsApi={favsApi}
      isMe={isMe}
    />
  );
};

/** `UserCell` for a user instead of a table row. */
export const UserComponent = ({
  user,
  favsApi,
  isMe,
  hasAnyFav
}: {
  user: AuthenticatedUser["user"] | TableOverviewUser["user"];
  favsApi?: TacticApiClient;
  isMe: boolean;
  hasAnyFav: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!user) {
    return null;
  }

  return (
    <div className="group relative flex min-h-full w-full items-center gap-2 py-4 md:border-b-0 md:py-0">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <UserAvatar
          user={{
            name: user.name,
            profile_photo_url: user.profile_photo_url,
            roles: "roles" in user ? user.roles : []
          }}
          size="s"
        />
        <div className={"items-left mr-5 flex min-w-0 flex-1 gap-2"}>
          <UserName userName={user.name} />
        </div>
        {!isMe && favsApi && (
          <div
            className="group relative flex items-center"
            // Point of this permanent hover is to show the favs component
            // once the user hovers over the user cell.
            // This will show the favs component.
            // We show a decoy before the hover to avoid unnecessary requests.
            // once we hover over the user cell, it make sense to keep original component visible.
            onMouseEnter={() => setIsHovered(true)}
            onTouchStart={() => setIsHovered(true)}
          >
            {isHovered ? (
              <div className="-translate-y-1/2 absolute top-1/2 right-0 flex flex-shrink-0 cursor-pointer items-center justify-center">
                {/* these are compatible with the Favs component */}
                <Favs
                  user={user as unknown as IUserFull}
                  api={favsApi}
                  size="sm"
                />
              </div>
            ) : (
              <div className="-translate-y-1/2 absolute top-1/2 right-0 flex flex-shrink-0 cursor-pointer items-center justify-center">
                <Star
                  className={classNames(
                    "h-4 w-4",
                    "transition duration-150 ease-in-out hover:text-primary-hover",
                    !hasAnyFav
                      ? "text-neutral-200"
                      : "fill-[#3DB4C4] text-[#3DB4C4]"
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex-shrink-0" />
    </div>
  );
};

/** A one-line name that shows the whole name in a tooltip when it is cut. */
export const UserNameComponent = ({
  userName,
  withoutProvider = false
}: {
  userName: string;
  withoutProvider?: boolean;
}) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Check if the text is truncated after render
  useEffect(() => {
    if (nameRef.current) {
      setIsTruncated(nameRef.current.scrollWidth > nameRef.current.clientWidth);
    }
  }, [userName]);

  // Create the span element for the user's name
  const nameSpan = (
    <span
      ref={nameRef}
      className="block w-full truncate text-left font-inter font-normal text-sm"
      onTouchStart={() => {
        if (isTruncated) {
          setIsOpen(true);
          // Auto-close after 2 seconds
          // setTimeout(() => setIsOpen(false), 2000);
        }
      }}
    >
      {userName}
    </span>
  );

  const tooltipContent = isTruncated ? (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>{nameSpan}</TooltipTrigger>
      <TooltipContent className="break-words max-w-xs">
        {userName}
      </TooltipContent>
    </Tooltip>
  ) : (
    nameSpan
  );

  return withoutProvider ? (
    tooltipContent
  ) : (
    <TooltipProvider>{tooltipContent}</TooltipProvider>
  );
};

export const UserName = React.memo(UserNameComponent);
